import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  SCBottomBarContainer,
  SCButtonWrapper,
  SCButtonCircle,
  SCButtonLabel,
  SCButtonsRow,
  SCButtonIcon,
} from 'components/AugmentedReality/PhotoEditor/BottomBar/styled'
import { FiDownload } from 'react-icons/fi';
import useCamera from 'components/AugmentedReality/hooks/useCamera'
import ConfirmModal from 'components/AugmentedReality/Structure/ConfirmModal'
import { SCDownloadToast, SCDownloadToastEmoji } from 'components/AugmentedReality/Experience/styled'
import { StickerType } from 'components/AugmentedReality/hooks/useStickers'
import { ARWatermarkType } from '../../../../types/augmentedReality'

interface BottomBarProps {
  currentOverlay: string;
  camera: ReturnType<typeof useCamera>;
  download: boolean;
  onDownload: (state: boolean) => void;
  onPrevious: () => void;
  onCancel: () => void;
  onGoLive: () => void;
  capturedPhoto: string;
  stickers: StickerType[];
  photoRef: any;
  overlayRef?: React.RefObject<HTMLImageElement>;
  frameRef?: React.RefObject<HTMLImageElement>;
  hideOverlay: boolean;
  frameUrl?: string;
  watermark?: ARWatermarkType;
}

const BottomBar = ({ currentOverlay, download, onDownload, onPrevious, onCancel, onGoLive, camera, capturedPhoto, stickers, photoRef, overlayRef, frameRef, hideOverlay, frameUrl, watermark }: BottomBarProps) => {
  const { t } = useTranslation()
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showDownloadToast, setShowDownloadToast] = useState(false);
  const [animateShare, setAnimateShare] = useState(false);
  const [animateDownload, setAnimateDownload] = useState(false);
  const [animateExit, setAnimateExit] = useState(false);

  const triggerAnimate = (setter: (v: boolean) => void) => {
    setter(true);
    setTimeout(() => setter(false), 750);
  };

  const generateImageWithOverlay = async (): Promise<string> => {
    return new Promise(async (resolve) => {
      const baseImg = new Image()
      const overlayImg = new Image()

      overlayImg.crossOrigin = 'anonymous'

      baseImg.src = capturedPhoto
      overlayImg.src = currentOverlay

      // Load frame image in parallel with other assets
      const frameImg = frameUrl ? new Image() : null
      if (frameImg) {
        frameImg.crossOrigin = 'anonymous'
        frameImg.src = frameUrl!
      }

      const loadPromises: Promise<any>[] = [
        new Promise(res => (baseImg.onload = res)),
        new Promise(res => (overlayImg.onload = res)),
      ]
      if (frameImg) {
        loadPromises.push(new Promise<void>(res => { frameImg.onload = () => res(); frameImg.onerror = () => res() }))
      }
      await Promise.all(loadPromises)

      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return resolve('')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      // Determine the container's displayed size to replicate object-fit: cover cropping
      const rect = photoRef.current?.getBoundingClientRect()
      const displayW = rect?.width ?? baseImg.width
      const displayH = rect?.height ?? baseImg.height

      // object-fit: cover scales the image so it fills the container,
      // cropping the excess from the center
      const coverScale = Math.max(displayW / baseImg.width, displayH / baseImg.height)
      const visibleNativeW = displayW / coverScale
      const visibleNativeH = displayH / coverScale
      const cropX = (baseImg.width - visibleNativeW) / 2
      const cropY = (baseImg.height - visibleNativeH) / 2

      // Canvas matches the native resolution of the visible crop area
      canvas.width = Math.round(visibleNativeW)
      canvas.height = Math.round(visibleNativeH)

      // Draw only the visible portion of the base image
      ctx.drawImage(baseImg, cropX, cropY, visibleNativeW, visibleNativeH, 0, 0, canvas.width, canvas.height)

      // Derive overlay position/size from the actual rendered element via getBoundingClientRect,
      // which accounts for the pan/zoom transform applied during calibration.
      let overlayX: number
      let overlayY: number
      let overlayW: number
      let overlayH: number

      if (overlayRef?.current && photoRef.current) {
        const overlayRect = overlayRef.current.getBoundingClientRect()
        const containerRect = photoRef.current.getBoundingClientRect()
        const canvasScale = canvas.width / containerRect.width
        overlayX = (overlayRect.left - containerRect.left) * canvasScale
        overlayY = (overlayRect.top - containerRect.top) * canvasScale
        overlayW = overlayRect.width * canvasScale
        overlayH = overlayRect.height * canvasScale
      } else {
        // Fallback: center the overlay using a fixed percentage
        const overlayRatio = overlayImg.width / overlayImg.height
        overlayW = canvas.width * 0.8
        overlayH = overlayW / overlayRatio
        if (overlayH > canvas.height * 0.5) {
          overlayH = canvas.height * 0.5
          overlayW = overlayH * overlayRatio
        }
        overlayX = (canvas.width - overlayW) / 2
        overlayY = (canvas.height - overlayH) / 2
      }

      // Replicate SCOverlayImage CSS mask (two gradients, mask-composite: intersect)
      const offscreen = document.createElement('canvas')
      offscreen.width = Math.round(overlayW)
      offscreen.height = Math.round(overlayH)
      const offCtx = offscreen.getContext('2d')!
      offCtx.drawImage(overlayImg, 0, 0, overlayW, overlayH)

      // Horizontal: transparent → black at 5%, black at 95% → transparent
      offCtx.globalCompositeOperation = 'destination-in'
      const hGrad = offCtx.createLinearGradient(0, 0, overlayW, 0)
      hGrad.addColorStop(0, 'transparent')
      hGrad.addColorStop(0.05, 'black')
      hGrad.addColorStop(0.95, 'black')
      hGrad.addColorStop(1, 'transparent')
      offCtx.fillStyle = hGrad
      offCtx.fillRect(0, 0, overlayW, overlayH)

      // Vertical: black → black at 95% → transparent (bottom fade only)
      const vGrad = offCtx.createLinearGradient(0, 0, 0, overlayH)
      vGrad.addColorStop(0, 'black')
      vGrad.addColorStop(0.95, 'black')
      vGrad.addColorStop(1, 'transparent')
      offCtx.fillStyle = vGrad
      offCtx.fillRect(0, 0, overlayW, overlayH)

      ctx.drawImage(offscreen, overlayX, overlayY, overlayW, overlayH)

      if (stickers.length && photoRef.current) {
        // Canvas and container now share the same crop region,
        // so sticker positions map with a single uniform scale
        const scale = canvas.width / displayW

        const loaded = await Promise.all(
          stickers.map(
            (sticker) =>
              new Promise<{ img: HTMLImageElement; sticker: StickerType }>((res) => {
                const img = new Image()
                img.crossOrigin = 'anonymous'
                img.onload = () => res({ img, sticker })
                img.onerror = () => res({ img, sticker })
                img.src = sticker.url
              })
          )
        )

        loaded.forEach(({ img, sticker }) => {
          if (!img.naturalWidth) return

          const x = sticker.x * scale
          const y = sticker.y * scale
          const w = sticker.width * scale
          const h = sticker.height * scale

          ctx.save()
          ctx.translate(x + w / 2, y + h / 2)
          ctx.rotate((sticker.rotation || 0) * Math.PI / 180)
          ctx.drawImage(img, -w / 2, -h / 2, w, h)
          ctx.restore()
        })
      }

      if (frameImg && frameImg.naturalWidth > 0) {
        ctx.drawImage(frameImg, 0, 0, canvas.width, canvas.height)
      }

      resolve(canvas.toDataURL('image/png'))
    })
  }

  const generateImageNoOverlay = async (): Promise<string> => {
    return new Promise(async (resolve) => {
      const baseImg = new Image()

      baseImg.src = capturedPhoto

      // Load frame image in parallel with other assets
      const frameImg = frameUrl ? new Image() : null
      if (frameImg) {
        frameImg.crossOrigin = 'anonymous'
        frameImg.src = frameUrl!
      }

      const loadPromises: Promise<any>[] = [
        new Promise((res) => (baseImg.onload = res)),
      ]
      if (frameImg) {
        loadPromises.push(new Promise<void>(res => { frameImg.onload = () => res(); frameImg.onerror = () => res() }))
      }
      await Promise.all(loadPromises)

      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return resolve('')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      // Determine the container's displayed size to replicate object-fit: cover cropping
      const rect = photoRef.current?.getBoundingClientRect()
      const displayW = rect?.width ?? baseImg.width
      const displayH = rect?.height ?? baseImg.height

      // object-fit: cover scales the image so it fills the container,
      // cropping the excess from the center
      const coverScale = Math.max(displayW / baseImg.width, displayH / baseImg.height)
      const visibleNativeW = displayW / coverScale
      const visibleNativeH = displayH / coverScale
      const cropX = (baseImg.width - visibleNativeW) / 2
      const cropY = (baseImg.height - visibleNativeH) / 2

      // Canvas matches the native resolution of the visible crop area
      canvas.width = Math.round(visibleNativeW)
      canvas.height = Math.round(visibleNativeH)

      // Draw only the visible portion of the base image
      ctx.drawImage(baseImg, cropX, cropY, visibleNativeW, visibleNativeH, 0, 0, canvas.width, canvas.height)

      if (stickers.length && photoRef.current) {
        // Canvas and container now share the same crop region,
        // so sticker positions map with a single uniform scale
        const scale = canvas.width / displayW

        const loaded = await Promise.all(
          stickers.map(
            (sticker) =>
              new Promise<{ img: HTMLImageElement; sticker: StickerType }>((res) => {
                const img = new Image()
                img.crossOrigin = 'anonymous'
                img.onload = () => res({ img, sticker })
                img.onerror = () => res({ img, sticker })
                img.src = sticker.url
              })
          )
        )

        loaded.forEach(({ img, sticker }) => {
          if (!img.naturalWidth) return

          const x = sticker.x * scale
          const y = sticker.y * scale
          const w = sticker.width * scale
          const h = sticker.height * scale

          ctx.save()
          ctx.translate(x + w / 2, y + h / 2)
          ctx.rotate(((sticker.rotation || 0) * Math.PI) / 180)
          ctx.drawImage(img, -w / 2, -h / 2, w, h)
          ctx.restore()
        })
      }

      if (frameImg && frameImg.naturalWidth > 0) {
        ctx.drawImage(frameImg, 0, 0, canvas.width, canvas.height)
      }

      if (!watermark?.noUse && watermark?.image) {
        const wmImg = new Image()
        wmImg.crossOrigin = 'anonymous'
        await new Promise<void>(res => { wmImg.onload = () => res(); wmImg.src = watermark.image })

        const maxW = canvas.width * 0.4
        const maxH = canvas.height * 0.3
        const ratio = wmImg.width / wmImg.height
        let w = maxW
        let h = w / ratio
        if (h > maxH) { h = maxH; w = h * ratio }

        const col = watermark.position % 3
        const row = Math.floor(watermark.position / 3)
        const x = col === 0 ? 0 : col === 1 ? (canvas.width - w) / 2 : canvas.width - w
        const y = row === 0 ? 0 : row === 1 ? (canvas.height - h) / 2 : canvas.height - h

        ctx.drawImage(wmImg, x, y, w, h)
      }

      resolve(canvas.toDataURL('image/png'))
    })
  }

  const handleDownload = async () => {
    const result = hideOverlay ? await generateImageNoOverlay() : await generateImageWithOverlay();
    const now = new Date();
    const filename = `capture-${now.toISOString()}.png`;

    const blob = dataURLtoBlob(result);
    const file = new File([blob], filename, { type: 'image/png' });

    const isIOSChrome = /CriOS/.test(navigator.userAgent);
    const isStandalonePWA =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true;
    const canShareFile = !!navigator.share && !!navigator.canShare?.({ files: [file] });

    if ((isIOSChrome || isStandalonePWA) && canShareFile) {
      try {
        await navigator.share({ files: [file] });
      } catch (e) {
        if (e instanceof Error && e.name !== 'AbortError') {
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
        }
      }
    } else if (isIOSChrome) {
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } else {
      // Use blob URL instead of data URL for broader browser compatibility
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }

    setShowDownloadToast(true);
    setTimeout(() => setShowDownloadToast(false), 2000);
  };

  const dataURLtoBlob = (dataURL: string): Blob => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(parts[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleShare = async () => {
    try {
      if (!navigator.share) {
        alert(t('augmentedReality.photoEditor.shareNotSupported'));
        return;
      }

      const result = await generateImageWithOverlay();
      const blob = dataURLtoBlob(result);
      const file = new File([blob], `selfie-${Date.now()}.png`, { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: t('augmentedReality.photoEditor.mySelfie'),
          text: t('augmentedReality.photoEditor.lookAtMySelfie'),
          files: [file]
        });
      } else {
        await navigator.share({
          title: t('augmentedReality.photoEditor.mySelfie'),
          text: t('augmentedReality.photoEditor.lookAtMySelfie'),
          url: window.location.href
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        alert(t('augmentedReality.photoEditor.shareError', { message: error.message }));
      }
    }
  };

  const handleGoLive = () => {
    onGoLive();
  };

  return (
    <>
      <SCBottomBarContainer>
        <SCButtonsRow>
          <SCButtonWrapper onClick={onPrevious}>
            <SCButtonCircle $size="normal">
              <SCButtonIcon src={'/static/ra/back2.svg'} alt="Retour" />
            </SCButtonCircle>
            <SCButtonLabel>{t('augmentedReality.back')}</SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper
            onClick={() => {
              triggerAnimate(setAnimateShare)
              handleShare()
            }}
          >
            <SCButtonCircle $size="normal" className={animateShare ? 'animate' : ''}>
              <SCButtonIcon src={'/static/ra/share.png'} alt="Share" />
            </SCButtonCircle>
            <SCButtonLabel>{t('augmentedReality.photoEditor.share')}</SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper
            onClick={async () => {
              triggerAnimate(setAnimateDownload)
              if (!download) {
                setShowDownloadConfirm(true)
              } else {
                await handleDownload()
              }
            }}
          >
            <SCButtonCircle $size="xlarge" className={animateDownload ? 'animate' : ''}>
              <FiDownload size={40} color="white" />
            </SCButtonCircle>
            <SCButtonLabel>{t('augmentedReality.experience.download')}</SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper onClick={handleGoLive}>
            <SCButtonCircle $size="normal">
              <SCButtonIcon src={'/static/ra/golive.png'} alt="Go live" />
            </SCButtonCircle>
            <SCButtonLabel>{t('augmentedReality.photoEditor.goLive')}</SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper
            onClick={() => {
              triggerAnimate(setAnimateExit)
              setShowExitConfirm(true)
            }}
          >
            <SCButtonCircle $size="normal" className={animateExit ? 'animate' : ''}>
              <SCButtonIcon src={'/static/ra/exit.svg'} alt="Sortir" />
            </SCButtonCircle>
            <SCButtonLabel>{t('augmentedReality.experience.exit')}</SCButtonLabel>
          </SCButtonWrapper>
        </SCButtonsRow>
      </SCBottomBarContainer>

      {showDownloadConfirm && (
        <ConfirmModal
          title={t('augmentedReality.experience.downloadPhoto')}
          description={t('augmentedReality.experience.downloadConfirmDesc')}
          onCancel={() => setShowDownloadConfirm(false)}
          onConfirm={() => {
            onDownload(true)
            setShowDownloadConfirm(false)
            handleDownload()
          }}
          labelCancel={t('augmentedReality.experience.iRefuse')}
          labelConfirm={t('augmentedReality.experience.iAccept')}
        />
      )}

      {showExitConfirm && (
        <ConfirmModal
          title={t('augmentedReality.experience.exitTitle')}
          description={t('augmentedReality.experience.exitDesc')}
          onCancel={() => {
            camera.stopCamera()
            onCancel()
            }
          }
          onConfirm={() => {
            setShowExitConfirm(false);
            }
          }
          labelCancel={t('augmentedReality.experience.takeBreak')}
          labelConfirm={t('augmentedReality.experience.continueExperience')}
        />
      )}

      {showDownloadToast && (
        <SCDownloadToast>
          <SCDownloadToastEmoji src={'/static/ra/yeux-etoiles.svg'} />
          {t('augmentedReality.experience.downloadToast')}
        </SCDownloadToast>
      )}
    </>
  )
}

export default BottomBar
